import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MaterialTypeCardInfoRapido } from 'app/entity/models/entity';
import { PickupMember, UserRapidoResourceSharingRequest } from 'app/entity/models/rapido-offer';
import { ModalQueueService } from 'app/list/services/modal-queue.service';
import { RapidoOffersService } from 'app/services/rapido-offers.service';
import {
  catchError,
  EMPTY,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  beginRapidoTabRequest,
  loadTabHoldComplete,
  rapidoTabRequested,
  updateShouldCloseModal,
} from 'user/actions/user-profile.actions';
import { GetItErrorDialogComponent } from 'user/components/get-it-error-dialog/get-it-error-dialog.component';
import { GetItRapidoOptionsDialogComponent } from 'user/components/get-it-rapido-options-dialog/get-it-rapido-options-dialog.component';
import { GetItRapidoSuccessDialogComponent } from 'user/components/get-it-rapido-success-dialog/get-it-rapido-success-dialog.component';
import {
  UserState,
} from 'user/reducers/user.reducer';

@Injectable()
export class GetItRapidoEffects {
  public beginRapidoTabRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(beginRapidoTabRequest),
      switchMap(({ payload }) => {
        if (!payload.rapidoOffer) {
          this.openErrorModal({ error: 'No rapido offer found' });
          return EMPTY;
        }
        return this.handleOptions(payload);
      }),
      filter((data) => !!data),
      switchMap((payload: MaterialTypeCardInfoRapido) => {
        const request = this.createRequestPayload(payload);
        return this.offersService.placeRequest(request).pipe(
          map((response) => {
            return response;
          }),
          catchError(this.errorHandler())
        );
      }),
      tap((response) => {
        this.store.dispatch(updateShouldCloseModal({ shouldCloseModal: true }));
        const modalRef = this.modalService.open(GetItRapidoSuccessDialogComponent, {
          centered: true,
          windowClass: 'confirm-modal',
        });
        modalRef.result.finally(() => {
          this.modalQueueService.startQueue();
        });
        modalRef.componentInstance.recordId = response.recordId;
      }),
      switchMap(({ requestId }) => {
        return of(rapidoTabRequested({ requestId }));
      })
    )
  );

  private handleOptions(payload: MaterialTypeCardInfoRapido): Observable<MaterialTypeCardInfoRapido> {
    const pickupLocations = payload.rapidoOffer.pickupLocations.map((l: PickupMember) => {
      return {
        code: l.value,
        name: l.description,
      };
    });
    pickupLocations.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
    );
    const selectedLocation = pickupLocations?.[0];
    const modalRef = this.modalService.open(GetItRapidoOptionsDialogComponent, {
      centered: true,
      windowClass: 'inspire-custom-modal',
    });
    modalRef.componentInstance.pickupLocations = pickupLocations;
    // TODO use real volumes
    // modalRef.componentInstance.volumes = [{code: 0, volume: 'vol1'}];
    modalRef.componentInstance.selectedLocationCode = selectedLocation?.code;
    return from(modalRef.componentInstance.formDataSubject).pipe(
      map((data: {pickupLocation: string}) => {
        this.store.dispatch(updateShouldCloseModal({ shouldCloseModal: true }));
        return { ...data, ...payload };
      }),
      catchError((_) => {
        this.modalQueueService.startQueue();
        return EMPTY;
      })
    );
  }

  private openErrorModal(error: any) {
    const modalRef = this.modalService.open(GetItErrorDialogComponent, {
      centered: true,
      windowClass: 'inspire-custom-modal',
    });
    modalRef.componentInstance.message = error?.error?.message;
  }

  private createRequestPayload(payload: MaterialTypeCardInfoRapido) {
    const pickupLocation = payload.pickupLocation;
    const pickHoldLocationDesc = payload.rapidoOffer?.pickupLocations?.find(l => l.value === pickupLocation)?.description;
    const rsOffer = payload.rapidoOffer;
    const user = payload.user;
    const request: UserRapidoResourceSharingRequest = {
      rsOfferDetails: rsOffer,
      userResourceSharingRequest: {
        user: {
          value: user.id,
          desc: user.name,
        },
        pickupLocationType: 'LIBRARY',
        format: 'PHYSICAL',
        pickupLocation: {
          value: pickupLocation,
          description: pickHoldLocationDesc,
        },
        title: payload.title,
        isbn: payload.isbn,
        author: payload.author,
        publicationYear: payload.publicationYear,
        citationType: 'BK',
        requestedMedia: 'ANY',
        ...(payload.volume && {
          volume: parseInt(payload?.volume),
        }),
        ...(payload.note && {
          rsNotes: [
            {
              content: payload.note,
              createdBy: user?.name,
              createdDate: new Date().toISOString().slice(0, 10),
            },
          ],
        }),
      },
      recordId: payload.recordId,
      recordType: 'CDI',
    };
    return request;
  }

    private errorHandler() {
    // eslint-disable-next-line no-console
    console.error('errorHandler');
      return (err: any) => {
        this.store.dispatch(loadTabHoldComplete());
        this.store.dispatch(updateShouldCloseModal({ shouldCloseModal: true }));
        const modalRef = this.modalService.open(GetItErrorDialogComponent,
          {
            centered: true,
            windowClass: 'inspire-custom-modal',
          });
        modalRef.componentInstance.message = err?.error?.message;
        modalRef.result.finally(() => {
          this.modalQueueService.startQueue();
        });
        return EMPTY;
      };
    }

  constructor(
    private readonly actions$: Actions,
    private readonly modalService: NgbModal,
    private readonly store: Store<UserState>,
    private readonly modalQueueService: ModalQueueService,
    private readonly offersService: RapidoOffersService,
  ) {}
}
